import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EntityFiltersActions } from 'actions';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { getCrossID, getRealID, getMappedFilters } from 'utils/filters';
import { getBackendBoolean } from 'utils/fm';
import { canViewEntityWithPermissions } from 'utils/entityPermissions';
import CompaniesConfig from 'containers/companies/EntityConfig';
import ContactsConfig from 'containers/contacts/TableConfig';
import ActivitiesConfig from 'containers/Activities/EntityConfig';
import OpportunitiesConfig from 'containers/opportunities/EntityConfig';
import SalesOrdersConfig from 'containers/SalesOrders/TableConfig';
import Tours, { CROSS_FILTERS_TOUR } from '../components/Tours';
import { getCrossFilterSectionHint } from '../utils/getCustomFilterSectionHint';

import { COMPANIES, CONTACTS, ACTIVITIES, OPPORTUNITIES, SALESORDERS } from 'constants/Entities';
import {
    ENTITIES_ALLOWED_FOR_CROSS_FILTERS_PER_ENTITY,
    ENTITIES_WITH_CROSS_FILTERS,
    FILTERS_TO_BE_SKIPPED_AS_CROSSFILTERS,
    SENT_TO_BE_AS_STANDARD_FILTERS,
} from '../constants';

const useCrossFilters = ({
    entity,
    filterKey,
    defaultCrossFilters,
    ignoreDefaultCrossFilters,
    beforeCrossSchema,
    beforeCrossExtraSchema,
    multiEnvironmentData = {},
}) => {
    const {
        cache,
        filters,
        operators,
        crossFilters,
        crossOperators,
        permissions,
        showTheTour,
        forceCrossFilters,
        hasUserFlow,
    } = useSelector((state) => {
        const filtersStateKey = filterKey || entity?.entity || '';
        const cache = state.config.components[`${filtersStateKey}_filters`] || {};
        const {
            filters = {},
            operators = {},
            crossFilters = {},
            crossOperators = {},
            forceCrossFilters = true,
        } = state.entityFilters?.[filtersStateKey] || {};
        const permission = state.config?.permission || {};
        return {
            cache,
            filters,
            operators,
            crossFilters,
            crossOperators,
            forceCrossFilters,
            permissions: {
                [ACTIVITIES.entity]: canViewEntityWithPermissions(permission, ACTIVITIES),
                [COMPANIES.entity]: canViewEntityWithPermissions(permission, COMPANIES),
                [CONTACTS.entity]: canViewEntityWithPermissions(permission, CONTACTS),
                [OPPORTUNITIES.entity]: canViewEntityWithPermissions(permission, OPPORTUNITIES),
                [SALESORDERS.entity]: canViewEntityWithPermissions(permission, SALESORDERS),
            },
            showTheTour: !state.config.components?.tours?.filters?.[CROSS_FILTERS_TOUR],
            hasUserFlow: getBackendBoolean(state.config.userData?.userFlow),
        };
    });

    const dispatch = useDispatch();

    const crossLabels = useMemo(
        () => ({
            [COMPANIES.entity]: getLiteral('title_accounts'),
            [CONTACTS.entity]: getLiteral('title_contacts'),
            [ACTIVITIES.entity]: getLiteral('title_activities'),
            [OPPORTUNITIES.entity]: getLiteral('title_opportunities'),
            [SALESORDERS.entity]: getLiteral('title_salesorders'),
        }),
        [],
    );

    const ENTITIES_ALLOWED = useMemo(
        () => (entity?.entity ? ENTITIES_ALLOWED_FOR_CROSS_FILTERS_PER_ENTITY[entity.entity] : []),
        [entity],
    );

    const ENTITIES_ALLOWED_PER_NAME = useMemo(
        () =>
            entity && ENTITIES_ALLOWED?.length
                ? ENTITIES_ALLOWED.map((entity) => entity?.entity)
                : [],
        [ENTITIES_ALLOWED, entity],
    );

    const CrossEntitiesConfig = useMemo(() => {
        let companiesConfig = {
            ...CompaniesConfig(),
            defaultCrossFilters: defaultCrossFilters?.[COMPANIES.entity]
                ? defaultCrossFilters?.[COMPANIES.entity]
                : CompaniesConfig().defaultCrossFilters || [],
        };

        let contactsConfig = {
            ...ContactsConfig(),
            defaultCrossFilters: defaultCrossFilters?.[CONTACTS.entity]
                ? defaultCrossFilters?.[CONTACTS.entity]
                : ContactsConfig().defaultCrossFilters || [],
        };

        let activitiesConfig = {
            ...ActivitiesConfig(),
            defaultCrossFilters: defaultCrossFilters?.[ACTIVITIES.entity]
                ? defaultCrossFilters?.[ACTIVITIES.entity]
                : ActivitiesConfig().defaultCrossFilters || [],
        };

        let opportunitiesConfig = {
            ...OpportunitiesConfig(),
            defaultCrossFilters: defaultCrossFilters?.[OPPORTUNITIES.entity]
                ? defaultCrossFilters?.[OPPORTUNITIES.entity]
                : OpportunitiesConfig().defaultCrossFilters || [],
        };

        let salesOrdersConfig = {
            ...SalesOrdersConfig(),
            defaultCrossFilters: defaultCrossFilters?.[SALESORDERS.entity]
                ? defaultCrossFilters?.[SALESORDERS.entity]
                : SalesOrdersConfig().defaultCrossFilters || [],
        };

        if (ignoreDefaultCrossFilters) {
            companiesConfig.defaultCrossFilters = [];
            contactsConfig.defaultCrossFilters = [];
            activitiesConfig.defaultCrossFilters = [];
            opportunitiesConfig.defaultCrossFilters = [];
            salesOrdersConfig.defaultCrossFilters = [];
        }

        return {
            [COMPANIES.entity]: {
                config: companiesConfig,
                icon: 'accounts',
                allowed: permissions[COMPANIES.entity],
                title: getLiteral('label_filters_cross_filters_section_account'),
            },
            [CONTACTS.entity]: {
                config: contactsConfig,
                icon: 'contacts',
                allowed: permissions[CONTACTS.entity],
                title: getLiteral('label_filters_cross_filters_section_contact'),
            },
            [ACTIVITIES.entity]: {
                config: activitiesConfig,
                icon: 'activities',
                allowed: permissions[ACTIVITIES.entity],
                title: getLiteral('label_filters_cross_filters_section_activity'),
            },
            [OPPORTUNITIES.entity]: {
                config: opportunitiesConfig,
                icon: 'opportunities',
                allowed: permissions[OPPORTUNITIES.entity],
                title: getLiteral('label_filters_cross_filters_section_opportunity'),
            },
            [SALESORDERS.entity]: {
                config: salesOrdersConfig,
                icon: 'salesOrders',
                allowed: permissions[SALESORDERS.entity],
                title: getLiteral('label_filters_cross_filters_section_salesorders'),
            },
        };
    }, [permissions, defaultCrossFilters, ignoreDefaultCrossFilters]);

    const hasCrossFilters = useMemo(() => {
        return entity && ENTITIES_WITH_CROSS_FILTERS.includes(entity?.trueName);
    }, [entity]);

    const crossFiltersTour = useMemo(() => {
        return !hasUserFlow && hasCrossFilters && showTheTour ? <Tours entity={entity} /> : null;
    }, [entity, hasCrossFilters, showTheTour, hasUserFlow]);

    const initCrossFilters = useCallback(() => {
        if (!entity) return null;

        const mappedPromises = ENTITIES_ALLOWED.reduce((obj, item) => {
            if (item.entity === entity.entity || !CrossEntitiesConfig[item.entity]?.allowed)
                return obj;
            obj[item.entity] = dispatch(EntityFiltersActions.initFilters(item));
            return obj;
        }, {});

        const entities = Object.keys(mappedPromises);
        const promises = Object.values(mappedPromises);

        return Promise.all(promises).then((results) => {
            const obj = {};
            for (let i = 0; i < entities.length; i++) {
                const [schema, extraFieldsTab] = results[i];
                let newSchema = schema;

                if (beforeCrossSchema) {
                    newSchema = beforeCrossSchema({ schema: newSchema, entity: entities[i] });
                }
                let newExtraFieldsTab = extraFieldsTab;
                if (beforeCrossExtraSchema) {
                    newExtraFieldsTab = beforeCrossExtraSchema({
                        tabs: newExtraFieldsTab,
                        entity: entities[i],
                    });
                }
                obj[entities[i]] = { schema: newSchema, extraFieldsTab: newExtraFieldsTab };
            }

            return obj;
        });
    }, [
        CrossEntitiesConfig,
        ENTITIES_ALLOWED,
        dispatch,
        entity,
        beforeCrossSchema,
        beforeCrossExtraSchema,
    ]);

    const getDefaultFilters = useCallback(({ crossSchema, defaultFilters, filtersToIgnore }) => {
        const newDefaultFilters = Object.entries(crossSchema).reduce(
            (arr, [entity, schema]) => {
                const filteredFilters = (schema.defaultFilters || []).filter(
                    (filter) => !filtersToIgnore.includes(filter),
                );
                const crossIDFilters = filteredFilters.map((filter) => getCrossID(filter, entity));
                return arr.concat(crossIDFilters);
            },
            [...defaultFilters],
        );
        return newDefaultFilters;
    }, []);

    const getCrossFilters = useCallback(
        ({ crossFilters, getMappedSchema, filtersToIgnore = [], visibleFilters = [] }) => {
            if (!entity) return;

            const entityName = entity.entity;
            const mappedVisibleFilters = getMappedFilters(visibleFilters, entityName) || {};
            let finalVisibleFilters = [...visibleFilters];

            const shouldAddDefaultCrossFilters =
                forceCrossFilters ||
                (mappedVisibleFilters &&
                    Object.keys(mappedVisibleFilters).filter((key) => key !== entityName).length ===
                        0);

            const getDefaultCrossFilters = (filters = [], crossEntity) =>
                filters.map((filter) => getCrossID(filter, crossEntity));

            const crossSchema = Object.entries(CrossEntitiesConfig).reduce(
                (obj, [key, { config, allowed, title }]) => {
                    if (key === entityName || !ENTITIES_ALLOWED_PER_NAME.includes(key) || !allowed)
                        return obj;

                    const hint = getCrossFilterSectionHint(entityName, key);
                    const { schema, extraFieldsTab } = crossFilters[key];

                    const {
                        defaultCrossFilters = {},
                        filtersAddButtonSchema = [],
                        hiddenFilters = [],
                    } = config;

                    const defaultFilters = defaultCrossFilters[entityName] || [];

                    const { schemaByFieldId: mappedSchema } = getMappedSchema(schema);

                    let cachedVisible = mappedVisibleFilters?.[key] || [];
                    let visibleFilters = cachedVisible ? [...cachedVisible] : [...defaultFilters];

                    visibleFilters = visibleFilters.filter(
                        (current) => !filtersToIgnore.includes(current),
                    );

                    const newDefaultFilters = defaultFilters.filter(
                        (current) => !filtersToIgnore.includes(current),
                    );

                    // Adding extra field groups
                    const groupedExtraFields = extraFieldsTab.reduce((groups, tab) => {
                        groups.push({
                            label: tab.title,
                            id: tab.title,
                            options: tab.fields.map((field) => field.id),
                        });
                        return groups;
                    }, []);

                    // Assume that if the user has not applied any cross-filters and has not completed the tour,
                    // they have never used cross-filters. Therefore, set the default state of cross-filters
                    // for each entity to visible to promote the feature.
                    if (shouldAddDefaultCrossFilters) {
                        finalVisibleFilters = [
                            ...finalVisibleFilters,
                            ...getDefaultCrossFilters(defaultFilters, key),
                        ];
                    }

                    finalVisibleFilters = finalVisibleFilters.filter(
                        (current) => !filtersToIgnore.includes(getRealID(current).id),
                    );

                    const filtersToSkip = [...FILTERS_TO_BE_SKIPPED_AS_CROSSFILTERS];
                    const finalHiddenFilters = [...hiddenFilters];
                    const relatedEntity = ENTITIES_ALLOWED.find((e) => e?.entity === key);

                    if (
                        multiEnvironmentData?.isMultiEnvironmentActive &&
                        relatedEntity?.isMultiEnvironment &&
                        relatedEntity?.environmentFilterField
                    ) {
                        filtersToSkip.push(relatedEntity.environmentFilterField);
                        finalHiddenFilters.push(relatedEntity.environmentFilterField);
                    }

                    const finalFiltersAddButtonSchema = filtersAddButtonSchema.map(
                        ({ label, options = [] }) => {
                            return {
                                label,
                                options: options.filter(
                                    (option) => !filtersToSkip.includes(option),
                                ),
                            };
                        },
                    );

                    obj[key] = {
                        schema,
                        mappedSchema,
                        visibleFilters,
                        hiddenFilters: finalHiddenFilters,
                        extraSchema: [
                            ...(finalFiltersAddButtonSchema || []),
                            ...groupedExtraFields,
                        ],
                        title,
                        hint,
                        useSections: true,
                        defaultFilters: newDefaultFilters,
                    };
                    return obj;
                },
                {},
            );

            // Remove possible duplicates from finalVisibleFilters
            finalVisibleFilters = [...new Set(finalVisibleFilters)];

            return { crossSchema, visibleFilters: finalVisibleFilters };
        },
        [
            CrossEntitiesConfig,
            ENTITIES_ALLOWED,
            ENTITIES_ALLOWED_PER_NAME,
            entity,
            forceCrossFilters,
            multiEnvironmentData,
        ],
    );

    const getFinalCrossSchema = useCallback(
        ({ crossSchema, getFieldsFormatted, getSubfilterSchema, quickfilters, visibleFilters }) => {
            const finalCrossSchema = Object.entries(crossSchema).reduce(
                (finalCrossSchema, [key, obj]) => {
                    const { hiddenFilters, mappedSchema, title, hint, useSections, extraSchema } =
                        obj;

                    const filters = visibleFilters?.[key];

                    const schema = filters
                        ? filters
                              .filter((filter) => !quickfilters?.[key]?.includes(filter))
                              .reduce((arr, current) => {
                                  if (!mappedSchema[current] || hiddenFilters.includes(current))
                                      return arr;

                                  let isRemovable = true;
                                  let isPinnable = true;
                                  let isFakeCrossFilter = false;
                                  let subfilterSchema;

                                  if (mappedSchema[current]?.hasOwnProperty('isRemovable')) {
                                      isRemovable = mappedSchema[current].isRemovable;
                                  }

                                  if (mappedSchema[current]?.hasOwnProperty('isPinnable')) {
                                      isPinnable = mappedSchema[current].isPinnable;
                                  }

                                  if (mappedSchema[current]?.hasOwnProperty('isFakeCrossFilter')) {
                                      isFakeCrossFilter = mappedSchema[current].isFakeCrossFilter;
                                  }

                                  if (mappedSchema[current]?.hasOwnProperty('subfilterKey')) {
                                      subfilterSchema = getSubfilterSchema({
                                          crossEntity: key,
                                          filter: current,
                                          key: mappedSchema[current].subfilterKey,
                                          schema: mappedSchema,
                                      });
                                  }

                                  arr.push({
                                      label: mappedSchema[current].description,
                                      id: mappedSchema[current].id,
                                      filterTooltip: mappedSchema[current].filterTooltip,
                                      fields: [...getFieldsFormatted(mappedSchema[current])],
                                      isRemovable,
                                      isPinnable,
                                      isFakeCrossFilter,
                                      entity: key,
                                      crossId: getCrossID(mappedSchema[current].id, key),
                                      subfilterSchema,
                                  });

                                  return arr;
                              }, [])
                        : [];

                    if (schema.length === 0) return finalCrossSchema;

                    finalCrossSchema[key] = { title, hint, useSections, schema, extraSchema };

                    return finalCrossSchema;
                },
                {},
            );

            return finalCrossSchema;
        },
        [],
    );

    const getHasCrossFiltersActive = useCallback((crossFilters) => {
        if (!crossFilters || Object.entries(crossFilters).length === 0) return false;
        return Object.keys(crossFilters).every((entity) => {
            return !(
                typeof crossFilters[entity] === 'object' &&
                crossFilters[entity] !== null &&
                Object.keys(crossFilters[entity]).length === 0
            );
        });
    }, []);

    const getCrossValues = useCallback(
        ({ crossFilters, crossSchema, getValues }) => {
            if (!crossSchema) return {};
            const crossValues = Object.entries(crossSchema).reduce((crossValues, [key, obj]) => {
                const { mappedSchema } = obj;
                const filters = crossFilters?.[key];
                const crossEntity = CrossEntitiesConfig[key].config.entity;
                if (!crossEntity || !filters || Object.entries(filters).length === 0)
                    return crossValues;

                return {
                    ...crossValues,
                    ...getValues({ filters, crossEntity, mappedSchema }),
                };
            }, {});

            return crossValues;
        },
        [CrossEntitiesConfig],
    );

    const getModalOptionsSchema = useCallback(
        ({ crossSchema, extraSchema, getExtraFiltersSchema, hiddenFilters }) => {
            const crossEntitiesSchema = [];
            const extraMultiSchema = [];
            const selectorsSchema = [];

            if (crossSchema && extraSchema) {
                extraMultiSchema.push({
                    id: entity.entity,
                    label: CrossEntitiesConfig[entity.entity].title,
                    schema: extraSchema,
                });

                Object.entries(crossSchema).forEach(([id, obj]) => {
                    if (id !== entity.entity) {
                        const { title: label, icon: iconType, config } = CrossEntitiesConfig[id];
                        const { schema, extraSchema } = obj;
                        crossEntitiesSchema.push({ id, label, iconType });
                        extraMultiSchema.push({
                            id,
                            label,
                            schema: getExtraFiltersSchema({
                                schema,
                                extraSchema,
                                entity: config.entity,
                                hiddenFilters,
                            }),
                        });
                    }
                });

                selectorsSchema.push({
                    label: getLiteral('label_related_entites'),
                    schema: crossEntitiesSchema,
                    selectAllLabel: getLiteral('label_selectall'),
                });
            }

            return {
                extraMultiSchema,
                selectorsSchema,
            };
        },
        [CrossEntitiesConfig, entity?.entity],
    );

    const totalActiveFilters = useMemo(() => {
        if (!entity) return 0;

        const entityName = entity.entity;

        let mappedVisibleFilters = getMappedFilters(cache.visibleFilters || [], entityName) || {};

        if (Object.keys(mappedVisibleFilters).length === 0) {
            mappedVisibleFilters = {
                [entityName]: Object.keys(filters || {}),
                ...Object.entries(crossFilters || {}).reduce((acc, [crossKey, value]) => {
                    acc[crossKey] = Object.keys(value || {});
                    return acc;
                }, {}),
            };
        }

        const countActiveFilters = ({ visibleFilters, filters, operators }) => {
            let activeFilterCount = 0;

            if (Object.entries(filters || {}).length) {
                const activeFilters = Object.keys(filters).filter((key) => {
                    return (
                        !filters[key]?.hideForCount &&
                        ([...visibleFilters, ...SENT_TO_BE_AS_STANDARD_FILTERS].includes(key) ||
                            !visibleFilters)
                    );
                });

                // Exclude 'search' filter from count if present
                activeFilterCount = activeFilters.length - (filters.search ? 1 : 0);
            }

            // Count operator keys that don't have corresponding filters
            if (Object.entries(operators || {}).length) {
                activeFilterCount += Object.keys(operators).filter((key) => !filters?.[key]).length;
            }

            return activeFilterCount;
        };

        return [entityName, ...ENTITIES_ALLOWED_PER_NAME].reduce((total, item) => {
            const isMainEntity = entityName === item;
            const itemFilters = isMainEntity ? filters : crossFilters[item];
            const itemOperators = isMainEntity ? operators : crossOperators[item];

            return (
                total +
                countActiveFilters({
                    visibleFilters: mappedVisibleFilters?.[item] || [],
                    filters: itemFilters || {},
                    operators: itemOperators || {},
                })
            );
        }, 0);
    }, [
        ENTITIES_ALLOWED_PER_NAME,
        cache.visibleFilters,
        crossFilters,
        crossOperators,
        entity,
        filters,
        operators,
    ]);

    return {
        crossFiltersTour,
        crossLabels,
        getCrossFilters,
        getCrossValues,
        getDefaultFilters,
        getFinalCrossSchema,
        getHasCrossFiltersActive,
        getModalOptionsSchema,
        hasCrossFilters,
        initCrossFilters,
        totalActiveFilters,
    };
};

export default useCrossFilters;
